'use client';

import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { faEnvelope, faPhone } from '@fortawesome/pro-light-svg-icons';
import { Branch } from '@medprev-web-app/api-graphql-client';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { Button } from '@/components/ui/button/button.component';
import { Icon } from '@/components/ui/icon.component';
import { Link } from '@/components/ui/link/link.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { branchDefaultWhatsappMessage } from '@/utils/consts/cta-whatsapp-messages.consts';
import { formatPhoneNumber, generateWhatsAppUrl } from '@/utils/formatters/phonenumber';
import { cn } from '@/utils/cn-tw-merge';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { formatBranchName } from '@/utils/formatters/branch';

interface BranchContactProps extends React.HTMLAttributes<HTMLDivElement> {
  branch?: Branch;
}

export const BranchContact = ({ branch, className }: BranchContactProps) => {
  return (
    <section className={cn(className, 'w-full flex flex-col gap-3')}>
      {branch?.email && (
        <div className="flex gap-2" data-testid={DataTestIdEnum.BRANCH_CONTACT_EMAIL}>
          <div className="flex items-start gap-2 mb-2">
            <Icon icon={faEnvelope} className="text-gray-500 mr-2 w-5 h-5 mt-1" />
          </div>
          <div className="flex flex-col gap-2 truncate">
            <Typography
              variant={TypographyVariant.BODY_TEXT}
              className="text-gray-700 font-semibold"
            >
              E-mail
            </Typography>

            <Button
              variant="text"
              className="text-green-500 font-semibold justify-start hover:underline"
            >
              <Link prefetch={false} href={`mailto:${branch.email}`} className="truncate">{branch.email}</Link>
            </Button>
          </div>
        </div>
      )}

      {branch?.phones && branch.phones.length > 0 && (
        <div className="flex gap-2" data-testid={DataTestIdEnum.BRANCH_CONTACT_PHONE}>
          <div className="flex items-start gap-2 mb-2">
            <Icon icon={faPhone} className="text-gray-500 mr-2 w-5 h-5 mt-1" />
          </div>
          <div className="flex flex-col gap-2 ">
            <Typography
              variant={TypographyVariant.BODY_TEXT}
              className="text-gray-700 font-semibold"
            >
              Telefones
            </Typography>

            <div className="flex flex-col md:flex-row md:gap-2 md:items-center flex-wrap">
              {branch.phones.map((phone, index) => (
                <div key={phone} className="flex items-center">
                  <Button
                    variant="text"
                    className="text-green-500 font-semibold justify-start hover:underline p-0 h-auto"
                  >
                    <Link prefetch={false} href={`tel:+55${phone}`}>{formatPhoneNumber(phone as string)}</Link>
                  </Button>
                  {index < branch.phones!.length - 1 && (
                    <span className="hidden md:block ml-2 text-gray-500">·</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {branch?.whatsapp && (
        <>
          <div className="flex gap-2" data-testid={DataTestIdEnum.BRANCH_CONTACT_WHATSAPP}>
            <div className="flex items-start gap-2 mb-2">
              <Icon icon={faWhatsapp} className="text-gray-500 mr-2 w-5 h-5 mt-1" />
            </div>
            <div className="flex flex-col gap-2 ">
              <Typography
                variant={TypographyVariant.BODY_TEXT}
                className="text-gray-700 font-semibold"
              >
                WhatsApp
              </Typography>

              <Typography
                variant={TypographyVariant.BODY_TEXT}
                className="text-gray-700 mb-3"
              >
                {formatPhoneNumber(branch.whatsapp)}
              </Typography>
            </div>
          </div>
          <Link
            prefetch={false}
            href={generateWhatsAppUrl(branch.whatsapp, branchDefaultWhatsappMessage)}
            target="_blank"
            rel="noopener noreferrer"
            data-analytics-event={AnalyticsEnum.CLICK_SEND_MESSAGE_BRANCH}
            data-analytics-properties={JSON.stringify({ unidade_nome: formatBranchName(branch?.name as string) })}
          >
            <Button
              startIcon={faWhatsapp}
              className="text-white bg-green-500 font-semibold justify-center h-12"
              data-testid={DataTestIdEnum.BRANCH_CONTACT_WHATSAPP_BUTTON}

            >
              Enviar mensagem
            </Button>
          </Link>
        </>
      )}
    </section>
  );
};
